import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sd } from '@sd/lib/sdPaths';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { CommandPalette } from '@/components/CommandPalette';
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  Ticket,
  Calendar,
  Download,
  Filter,
  FileText,
  RefreshCw,
  RotateCcw,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { AuditTrailTable, UserRole } from '@/components/tickets/AuditTrailTable';
import { AuditLogEntry } from '@/types/ticket';

interface MetricCard {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}

const metrics: MetricCard[] = [
  { label: 'Total Tickets', value: '1,247', icon: Ticket },
  { label: 'Avg. Resolution Time', value: '4.2h', icon: Clock },
  { label: 'First Response SLA', value: '94.2%', icon: CheckCircle },
  { label: 'Customer Satisfaction', value: '4.6/5', icon: Users },
];

const ticketsByStatus = [
  { status: 'Open', count: 48, percentage: 28, color: 'bg-blue-500' },
  { status: 'Pending', count: 32, percentage: 19, color: 'bg-warning' },
  { status: 'Resolved', count: 67, percentage: 39, color: 'bg-success' },
  { status: 'Closed', count: 24, percentage: 14, color: 'bg-muted-foreground' },
];

const ticketsByPriority = [
  { priority: 'P1 - Critical', count: 12, percentage: 7, color: 'bg-destructive' },
  { priority: 'P2 - High', count: 45, percentage: 26, color: 'bg-warning' },
  { priority: 'P3 - Medium', count: 78, percentage: 46, color: 'bg-blue-500' },
  { priority: 'P4 - Low', count: 36, percentage: 21, color: 'bg-muted-foreground' },
];

const topIssues = [
  { issue: 'VPN Connection Issues', count: 45, trend: 'up' },
  { issue: 'Password Reset', count: 38, trend: 'down' },
  { issue: 'Email Problems', count: 32, trend: 'up' },
  { issue: 'Software Installation', count: 28, trend: 'same' },
  { issue: 'Hardware Malfunction', count: 24, trend: 'down' },
];

// Sample audit log entries for the audit trail
const sampleAuditEntries: AuditLogEntry[] = [
  { id: 'a1', action: 'created', actor: 'John Smith', timestamp: 'Jan 19, 2024 9:00 AM', details: 'Ticket TKT-10026' },
  { id: 'a2', action: 'viewed', actor: 'Emma Watson', timestamp: 'Jan 19, 2024 9:05 AM', details: '' },
  { id: 'a3', action: 'assigned', actor: 'System', timestamp: 'Jan 19, 2024 9:06 AM', details: '', previousValue: 'Unassigned', newValue: 'James Park' },
  { id: 'a4', action: 'priority_change', actor: 'James Park', timestamp: 'Jan 19, 2024 9:15 AM', details: '', previousValue: 'P3', newValue: 'P2' },
  { id: 'a5', action: 'note_added', actor: 'James Park', timestamp: 'Jan 19, 2024 9:30 AM', details: 'internal' },
  { id: 'a6', action: 'call_initiated', actor: 'James Park', timestamp: 'Jan 19, 2024 10:00 AM', details: '+1 (555) 123-0026' },
  { id: 'a7', action: 'attachment_added', actor: 'User 27', timestamp: 'Jan 19, 2024 10:30 AM', details: 'screenshot.png' },
  { id: 'a8', action: 'sla_paused', actor: 'James Park', timestamp: 'Jan 19, 2024 11:00 AM', details: 'Waiting on Customer' },
  { id: 'a9', action: 'viewed', actor: 'Sarah Chen', timestamp: 'Jan 19, 2024 11:15 AM', details: '' },
  { id: 'a10', action: 'sla_resumed', actor: 'James Park', timestamp: 'Jan 19, 2024 2:00 PM', details: '' },
  { id: 'a11', action: 'email_sent', actor: 'James Park', timestamp: 'Jan 19, 2024 2:05 PM', details: 'user27@healthcareplus.com' },
  { id: 'a12', action: 'status_change', actor: 'James Park', timestamp: 'Jan 19, 2024 2:30 PM', details: '', previousValue: 'open', newValue: 'pending' },
  { id: 'a13', action: 'escalated', actor: 'James Park', timestamp: 'Jan 19, 2024 3:00 PM', details: '', newValue: 'Tier 2' },
  { id: 'a14', action: 'viewed', actor: 'Mike Brown', timestamp: 'Jan 19, 2024 3:05 PM', details: '' },
  { id: 'a15', action: 'note_added', actor: 'Mike Brown', timestamp: 'Jan 19, 2024 3:30 PM', details: 'public' },
  { id: 'a16', action: 'resolved', actor: 'Mike Brown', timestamp: 'Jan 19, 2024 4:00 PM', details: 'FIXED' },
  { id: 'a17', action: 'viewed', actor: 'User 27', timestamp: 'Jan 19, 2024 4:15 PM', details: '' },
  { id: 'a18', action: 'closed', actor: 'System', timestamp: 'Jan 19, 2024 5:00 PM', details: 'Auto-closed after confirmation' },
  { id: 'a19', action: 'reopened', actor: 'User 27', timestamp: 'Jan 20, 2024 9:00 AM', details: 'Issue Recurred' },
  { id: 'a20', action: 'assigned', actor: 'System', timestamp: 'Jan 20, 2024 9:01 AM', details: '', previousValue: 'Mike Brown', newValue: 'James Park' },
];

const Reports = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('analytics');
  const [userRole, setUserRole] = useState<UserRole>('manager');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const handleExport = (format: 'csv' | 'pdf') => {
    toast.success(`Exporting ${format.toUpperCase()}...`, {
      description: 'Your report will be downloaded shortly',
    });
  };

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <Header onSearchClick={() => setCommandPaletteOpen(true)} />
      
      <CommandPalette 
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onCreateTicket={() => navigate(sd('tickets/new'))} 
        onSelectTicket={() => {}}
      />
      
      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeQueue="all"
          onQueueChange={() => {}}
          activeTab="tickets"
          onTabChange={(tab) => {
            if (tab === 'tickets') navigate(sd('tickets'));
            if (tab === 'assets') navigate(sd('assets'));
          }}
        />
        
        <ScrollArea className="flex-1">
          <div className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-5 sm:space-y-8">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-bold text-foreground sm:text-2xl">Analytics & Reports</h1>
                <p className="text-sm text-muted-foreground mt-0.5 sm:mt-1">Track your team's performance and ticket metrics</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-36 sm:w-40">
                    <Calendar className="h-4 w-4 mr-2 shrink-0" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="1y">Last year</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExport('csv')}>
                  <FileText className="h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExport('pdf')}>
                  <Download className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 sm:max-w-md">
                <TabsTrigger value="analytics" className="gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="audit" className="gap-2">
                  <Shield className="h-4 w-4" />
                  Audit Trail
                </TabsTrigger>
              </TabsList>

              <TabsContent value="analytics" className="space-y-5 mt-5 sm:space-y-8 sm:mt-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              {metrics.map((metric) => {
                const Icon = metric.icon;

                return (
                  <div 
                    key={metric.label}
                    className="p-4 rounded-xl border border-border bg-card sm:p-6"
                  >
                    <div className="flex items-center mb-3 sm:mb-4">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center sm:h-10 sm:w-10">
                        <Icon className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                      </div>
                    </div>
                    <div className="space-y-0.5 sm:space-y-1">
                      <p className="text-xl font-bold text-foreground sm:text-2xl">{metric.value}</p>
                      <p className="text-xs text-muted-foreground sm:text-sm">{metric.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
              {/* Tickets by Status */}
              <div className="p-4 rounded-xl border border-border bg-card sm:p-6">
                <h3 className="text-base font-semibold text-foreground mb-4 sm:text-lg sm:mb-6">Tickets by Status</h3>
                <div className="space-y-4">
                  {ticketsByStatus.map((item) => (
                    <div key={item.status} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{item.status}</span>
                        <span className="text-muted-foreground">{item.count} ({item.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={cn("h-full rounded-full", item.color)}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tickets by Priority */}
              <div className="p-4 rounded-xl border border-border bg-card sm:p-6">
                <h3 className="text-base font-semibold text-foreground mb-4 sm:text-lg sm:mb-6">Tickets by Priority</h3>
                <div className="space-y-4">
                  {ticketsByPriority.map((item) => (
                    <div key={item.priority} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{item.priority}</span>
                        <span className="text-muted-foreground">{item.count} ({item.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={cn("h-full rounded-full", item.color)}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Issues */}
            <div className="p-4 rounded-xl border border-border bg-card sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base font-semibold text-foreground sm:text-lg">Top Issues</h3>
                <RefreshCw className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-3">
                {topIssues.map((item, index) => (
                  <div 
                    key={item.issue}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-xs font-mono text-muted-foreground w-4">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">{item.issue}</p>
                    </div>
                    <span className="text-sm font-medium text-foreground">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* SLA Performance & Reopen Rate */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
              {/* SLA Performance */}
              <div className="p-4 rounded-xl border border-border bg-card sm:p-6">
                <h3 className="text-base font-semibold text-foreground mb-4 sm:text-lg sm:mb-6">SLA Compliance Summary</h3>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground">First Response SLA</span>
                      <span className="text-sm font-medium text-success">94.2%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-success rounded-full" style={{ width: '94.2%' }} />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground">Resolution SLA</span>
                      <span className="text-sm font-medium text-success">89.5%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-success rounded-full" style={{ width: '89.5%' }} />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground">Next Response SLA</span>
                      <span className="text-sm font-medium text-warning">78.3%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-warning rounded-full" style={{ width: '78.3%' }} />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="h-4 w-4 text-warning" />
                      <span>5 tickets currently breaching SLA</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time to Resolution & Reopen Rate */}
              <div className="p-4 rounded-xl border border-border bg-card sm:p-6">
                <h3 className="text-base font-semibold text-foreground mb-4 sm:text-lg sm:mb-6">Resolution Metrics</h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-6">
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-2xl font-bold text-foreground">4.2h</p>
                    <p className="text-xs text-muted-foreground mt-1">Avg. Resolution Time</p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center mx-auto mb-3">
                      <RotateCcw className="h-5 w-5 text-warning" />
                    </div>
                    <p className="text-2xl font-bold text-foreground">3.2%</p>
                    <p className="text-xs text-muted-foreground mt-1">Reopen Rate</p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="h-5 w-5 text-success" />
                    </div>
                    <p className="text-2xl font-bold text-foreground">87%</p>
                    <p className="text-xs text-muted-foreground mt-1">First Contact Resolution</p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Ticket className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-2xl font-bold text-foreground">42</p>
                    <p className="text-xs text-muted-foreground mt-1">Tickets/Agent/Day</p>
                  </div>
                </div>
              </div>
            </div>
              </TabsContent>

              {/* Audit Trail Tab */}
              <TabsContent value="audit" className="mt-6">
                <div className="space-y-4">
                  {/* Role Selector for Demo */}
                  <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border sm:gap-3 sm:p-4">
                    <span className="text-sm text-muted-foreground shrink-0">View as:</span>
                    <Select value={userRole} onValueChange={(v) => setUserRole(v as UserRole)}>
                      <SelectTrigger className="w-32 sm:w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="auditor">Auditor</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">
                      (Different roles see different audit events)
                    </span>
                  </div>

                  <AuditTrailTable
                    entries={sampleAuditEntries}
                    userRole={userRole}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </ScrollArea>
      </div>
    </div>
  );
};

export default Reports;
